import { useLocale } from '../i18n/LocaleContext';
import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';

const getContactApiUrl = () => {
  const configuredBaseUrl = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/+$/, '');
  if (configuredBaseUrl) return `${configuredBaseUrl}/api/contact`;

  if (process.env.NODE_ENV === 'development') {
    return 'http://localhost:10000/api/contact';
  }

  return 'https://api.noteician.com/api/contact';
};

const ContactPage = () => {
  const { t } = useLocale();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  // Email validation function
  const isValidEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(String(email).toLowerCase());
  };

  // Handle form field changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error message when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  // Validate the form fields
  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = t('contact.nameRequired');
    }

    if (!formData.email.trim()) {
      newErrors.email = t('contact.emailRequired');
    } else if (!isValidEmail(formData.email)) {
      newErrors.email = t('contact.emailInvalid');
    }

    if (!formData.subject.trim()) {
      newErrors.subject = t('contact.subjectRequired');
    }

    if (!formData.message.trim()) {
      newErrors.message = t('contact.messageRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();


    // First validate the form
    if (!validateForm()) {

      return;
    }

    setSubmitting(true);
    setSubmitStatus(null);

    try {
      const contactApiUrl = getContactApiUrl();

      // Send form data to the server using fetch
      const response = await fetch(contactApiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });


      // Error responses from proxies/static hosts are not always JSON.
      const contentType = response.headers.get('content-type') || '';
      const result = contentType.includes('application/json')
        ? await response.json()
        : {};


      if (!response.ok) {

        throw new Error(result.message || `Failed to send message (${response.status})`);
      }

      // Reset form on success
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: ''
      });

      setSubmitStatus({
        type: 'success',
        message: t('contact.success')
      });

    } catch (error) {
      console.error('Error sending message:', error);
      setSubmitStatus({
        type: 'error',
        message: t('contact.error')
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ContactSection>
      <SEO
        title={t("nav.contact")}
        description={t("seo.contact")}
        canonicalPath="/contact"
      />
      <div className="container">
        <SectionTitle>{t('contact.title')}</SectionTitle>
        <SectionSubtitle>
          {t('contact.subtitle')}
        </SectionSubtitle>

        <ContactContainer>
          <ContactForm
            as={motion.form}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            onSubmit={handleSubmit}
          >
            <FormTitle>{t('contact.formTitle')}</FormTitle>

            {submitStatus && (
              <StatusMessage $type={submitStatus.type}>
                {submitStatus.message}
              </StatusMessage>
            )}

            <FormGroup>
              <FormLabel htmlFor="contact-name">{t("contact.name")}</FormLabel>
              <FormInput dir="auto"
                type="text"
                id="contact-name" name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder={t("contact.namePlaceholder")}
                $hasError={errors.name}
              />
              {errors.name && <ErrorMessage>{errors.name}</ErrorMessage>}
            </FormGroup>

            <FormGroup>
              <FormLabel htmlFor="contact-email">Email</FormLabel>
              <FormInput dir="auto"
                type="email"
                id="contact-email" name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder={t("contact.emailPlaceholder")}
                $hasError={errors.email}
              />
              {errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
            </FormGroup>

            <FormGroup>
              <FormLabel htmlFor="contact-subject">{t("contact.subject")}</FormLabel>
              <FormInput dir="auto"
                type="text"
                id="contact-subject" name="subject"
                value={formData.subject}
                onChange={handleChange}
                placeholder={t("contact.subjectPlaceholder")}
                $hasError={errors.subject}
              />
              {errors.subject && <ErrorMessage>{errors.subject}</ErrorMessage>}
            </FormGroup>

            <FormGroup>
              <FormLabel htmlFor="contact-message">{t("contact.message")}</FormLabel>
              <FormTextarea dir="auto"
                id="contact-message" name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder={t("contact.messagePlaceholder")}
                rows={5}
                $hasError={errors.message}
              />
              {errors.message && <ErrorMessage>{errors.message}</ErrorMessage>}
            </FormGroup>

            <SubmitButton
              type="submit"
              as={motion.button}
              whileHover={{ y: -5 }}
              whileTap={{ scale: 0.95 }}
              disabled={submitting}
            >
              {submitting ? t('contact.sending') : t('contact.send')}
            </SubmitButton>
          </ContactForm>
        </ContactContainer>
      </div>
    </ContactSection>
  );
};

const ContactSection = styled.section`
  padding: 100px 0;
  background-color: var(--white);
`;

const StatusMessage = styled.div`
  padding: 12px 15px;
  margin-bottom: 20px;
  border-radius: 8px;
  font-weight: 500;
  background-color: ${props => props.$type === 'success' ? 'rgba(0, 128, 0, 0.1)' : 'rgba(255, 0, 0, 0.1)'};
  color: ${props => props.$type === 'success' ? 'green' : 'red'};
  border-inline-start: 4px solid ${props => props.$type === 'success' ? 'green' : 'red'};
`;

const ErrorMessage = styled.div`
  color: #e74c3c;
  font-size: 0.85rem;
  margin-top: 5px;
  font-weight: 500;
`;

const SectionTitle = styled.h1`
  font-size: 2.5rem;
  margin-bottom: 20px;
  font-weight: 700;
  color: var(--primary);
  text-align: center;
`;

const SectionSubtitle = styled.p`
  font-size: 1.2rem;
  margin-bottom: 60px;
  max-width: 800px;
  margin-inline-start: auto;
  margin-inline-end: auto;
  text-align: center;
  color: var(--secondary);
`;

const ContactContainer = styled.div`
  display: flex;
  justify-content: center;
  max-width: 800px;
  margin: 0 auto;
`;

const ContactForm = styled.form`
  flex: 1;
  background-color: white;
  border-radius: 12px;
  padding: 40px;
  box-shadow: var(--shadow);
  border: 1px solid rgba(0, 0, 0, 0.05);
`;

const FormTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 30px;
  color: var(--primary);
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const FormLabel = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: var(--dark-gray);
`;

const FormInput = styled.input`
  width: 100%;
  padding: 12px 15px;
  border: 1px solid ${props => props.$hasError ? '#e74c3c' : 'rgba(0, 0, 0, 0.1)'};
  border-radius: 8px;
  font-size: 1rem;
  transition: var(--transition);

  &:focus {
    outline: none;
    border-color: ${props => props.$hasError ? '#e74c3c' : 'var(--primary)'};
    box-shadow: 0 0 0 2px ${props => props.$hasError ? 'rgba(231, 76, 60, 0.1)' : 'rgba(0, 128, 128, 0.1)'};
  }
`;

const FormTextarea = styled.textarea`
  width: 100%;
  padding: 12px 15px;
  border: 1px solid ${props => props.$hasError ? '#e74c3c' : 'rgba(0, 0, 0, 0.1)'};
  border-radius: 8px;
  font-size: 1rem;
  resize: vertical;
  transition: var(--transition);

  &:focus {
    outline: none;
    border-color: ${props => props.$hasError ? '#e74c3c' : 'var(--primary)'};
    box-shadow: 0 0 0 2px ${props => props.$hasError ? 'rgba(231, 76, 60, 0.1)' : 'rgba(0, 128, 128, 0.1)'};
  }
`;

const SubmitButton = styled.button`
  display: inline-block;
  padding: 12px 30px;
  background: var(--gradient);
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  transition: var(--transition);
  box-shadow: var(--shadow);
  cursor: pointer;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
    box-shadow: var(--shadow);
  }
`;

export default ContactPage;
